import { Link, useLocation } from 'react-router-dom';
import { IconCar, IconHome, IconSearch, IconShieldCheck, IconUserCircle } from '@tabler/icons-react';
import type { ComponentType } from 'react';
import { RIDES_ROLES } from '@actschurch/ccms-sdk';
import { useEnrichedAuth } from '@/hooks/useEnrichedAuth';
import { useRidesSettings } from '@/lib/settings';
import { useMeSummary } from '@/lib/me';
import { todayYmd } from '@/lib/format';
import { useIsFocusMode } from '@/components/shell/focusMode';
import { isPublicRoute, isStepRoute } from '@/components/shell/routes';

/**
 * The tab bar shows on phones (and in the installed app) for every signed-in screen except step
 * flows and screens in focus mode (a sticky action bar or the new-member profile form is on
 * screen), so only one bar ever sits at the bottom. Desktop uses the header text navigation.
 */
export function useBottomTabBarVisible(): boolean {
  const { pathname } = useLocation();
  const { isAuthenticated } = useEnrichedAuth();
  const { settings } = useRidesSettings();
  const focus = useIsFocusMode();
  if (!isAuthenticated || settings.maintenanceMode || focus) return false;
  if (isPublicRoute(pathname)) return false;
  return !isStepRoute(pathname);
}

export interface MainNavItem {
  key: string;
  label: string;
  to: string;
  icon: ComponentType<{ size?: number; stroke?: number }>;
  active: (path: string) => boolean;
  /** Small orange dot: a ride or lift is happening today. */
  dot?: boolean;
}

/** The main destinations, shared by the phone tab bar and the desktop header navigation. */
export function useMainNavItems(): MainNavItem[] {
  const { hasAnyRole, isAuthenticated } = useEnrichedAuth();
  const { settings } = useRidesSettings();
  const { data: summary } = useMeSummary();
  if (!isAuthenticated) return [];

  const today = todayYmd();
  const rideToday = summary?.nextBooking?.date === today || summary?.nextListing?.date === today;
  const isSecurity = settings.securityViewEnabled && hasAnyRole([RIDES_ROLES.RidesSecurity, RIDES_ROLES.RidesAdmin, RIDES_ROLES.Admin]);
  const isRideDay = (p: string) => p.startsWith('/ride/day/') || p.startsWith('/ride/confirm/');

  return [
    { key: 'home', label: 'Home', to: '/home', icon: IconHome, active: (p) => p === '/home' || isRideDay(p), dot: rideToday },
    ...(settings.riderRegistrationOpen
      ? [{ key: 'ride', label: 'Find a ride', to: '/ride/event', icon: IconSearch, active: (p: string) => p.startsWith('/ride') && !isRideDay(p) }]
      : []),
    ...(settings.driverRegistrationOpen || summary?.hasDriverProfile
      ? [{
          key: 'drive',
          label: 'Drive',
          to: summary?.hasDriverProfile ? '/drive/listings' : '/drive/profile',
          icon: IconCar,
          active: (p: string) => p.startsWith('/drive'),
        }]
      : []),
    ...(isSecurity ? [{ key: 'security', label: 'Security', to: '/security', icon: IconShieldCheck, active: (p: string) => p.startsWith('/security') }] : []),
    { key: 'account', label: 'Account', to: '/account', icon: IconUserCircle, active: (p) => p.startsWith('/account') },
  ];
}

/** Mobile bottom navigation for the member portal. */
export function BottomTabBar() {
  const { pathname } = useLocation();
  const visible = useBottomTabBarVisible();
  const tabs = useMainNavItems();
  if (!visible || !tabs.length) return null;

  return (
    <nav className="rides-tabbar" aria-label="Main">
      {tabs.map((tab) => {
        const on = tab.active(pathname);
        const Icon = tab.icon;
        return (
          <Link key={tab.key} to={tab.to} className={`rides-tab${on ? ' on' : ''}`} aria-current={on ? 'page' : undefined}>
            <span className="ic" aria-hidden="true">
              <Icon size={24} stroke={on ? 2.2 : 1.8} />
              {tab.dot && <span className="dot" />}
            </span>
            <span className="lb">{tab.label}</span>
            {tab.dot && <span className="sr-only">(ride today)</span>}
          </Link>
        );
      })}
    </nav>
  );
}
